import { App, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import { getLocalizedMessage } from "@pimo/pimo-l10n";

import { TestAppState } from "../../app";
import { getLocalizedCardCopy } from "../../utils/get-localized-card-copy";
import {
  getItemName,
  getPluralOfItemName,
} from "../../utils/get-plural-of-item-name";
import {
  getPluralOfProjectName,
  getProjectName,
} from "../../utils/get-plural-of-project-name";

/**
 * Everything the dashboard cards used to read off `this` and off the locals at
 * the top of `buildDashboardView()`.
 *
 * The cards were lifted out of one 1500-line method (#2222); they all derive
 * their titles and descriptions from the same four names and the same
 * `getCardCopy` closure, so those are computed once here and handed to each
 * card rather than recomputed 34 times.
 */
export type DashboardCardContext = {
  view: View<TestAppState, DragAndDropLayout>;
  app: App<TestAppState>;
  settings: TestAppState["settings"];
  language: TestAppState["language"];
  itemName: ReturnType<typeof getItemName>;
  itemNamePlural: ReturnType<typeof getPluralOfItemName>;
  projectName: ReturnType<typeof getProjectName>;
  projectNamePlural: ReturnType<typeof getPluralOfProjectName>;
  getCardCopy: (
    currentLanguage: string,
    key: Parameters<typeof getLocalizedCardCopy>[1],
    params?: Parameters<typeof getLocalizedCardCopy>[2]
  ) => ReturnType<typeof getLocalizedCardCopy>;
};

export function createDashboardCardContext(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
): DashboardCardContext {
  const state = app.getAppState();
  const { settings, language } = state;
  const itemName = getItemName(settings?.configuration?.itemName, language);
  const itemNamePlural = getPluralOfItemName(
    settings?.configuration?.itemName,
    language
  );
  const projectName = getProjectName(
    settings?.configuration?.projectName,
    language
  );
  const projectNamePlural = getPluralOfProjectName(
    settings?.configuration?.projectName,
    language
  );
  const categoryLabel =
    settings?.configuration?.fieldsDefinition?.find(
      (field) => field.fieldName === "category"
    )?.label ?? getLocalizedMessage(language, "category");
  const getCardCopy = (
    currentLanguage: string,
    key: Parameters<typeof getLocalizedCardCopy>[1],
    params: Parameters<typeof getLocalizedCardCopy>[2] = {}
  ) =>
    getLocalizedCardCopy(currentLanguage, key, {
      itemName,
      itemNamePlural,
      projectName,
      projectNamePlural,
      categoryLabel,
      ...params,
    });

  return {
    view,
    app,
    settings,
    language,
    itemName,
    itemNamePlural,
    projectName,
    projectNamePlural,
    getCardCopy,
  };
}
